import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { Download } from "lucide-react";
import { label } from "../labels";
import { useStudentPhoto } from "../hooks";

function Avatar({ student, photoUrl }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={`Foto de ${student.firstName}`}
        className="h-20 w-20 rounded-2xl object-cover border-2 border-[#F6EFDF]/30"
        data-testid="eid-card-photo"
      />
    );
  }
  return (
    <div className="h-20 w-20 rounded-2xl bg-[#F6EFDF]/10 border border-[#F6EFDF]/20 flex items-center justify-center font-serif text-2xl">
      {student.firstName?.[0]}
      {student.lastName?.[0]}
    </div>
  );
}

function CardField({ title, value, testId }) {
  return (
    <div>
      <div className="opacity-60 uppercase tracking-widest text-[9px]">{title}</div>
      <div className="font-medium text-[12px] mt-0.5" data-testid={testId}>
        {value || "—"}
      </div>
    </div>
  );
}

export default function IdCard({ student, publicUrl, downloadable = false, photoVersion = 0 }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const photoUrl = useStudentPhoto(student?.id, student?.hasPhoto, photoVersion);
  if (!student) return null;

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 3, cacheBust: true });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `carnet-${student.studentCode}.png`;
      a.click();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full max-w-[360px]">
      <div
        ref={ref}
        className="rounded-3xl bg-[#132C44] text-[#F6EFDF] p-6 shadow-xl shadow-[#132C44]/25 relative overflow-hidden"
        data-testid="eid-id-card"
      >
        <div className="absolute -top-10 -right-8 h-40 w-40 rounded-full bg-[#8AA986]/15 pointer-events-none" />
        <div className="absolute -bottom-16 -left-10 h-36 w-36 rounded-full bg-[#B08D3C]/10 pointer-events-none" />
        <div className="flex items-start justify-between relative">
          <span className="text-[10px] tracking-widest font-semibold opacity-80">PINAR ENGLISH · ID</span>
          <span className="h-8 w-8 rounded-md bg-[#8AA986] text-[#132C44] font-bold flex items-center justify-center text-[11px]">
            PE
          </span>
        </div>
        <div className="mt-5 flex items-center gap-4 relative">
          <Avatar student={student} photoUrl={photoUrl} />
          <div className="min-w-0">
            <h3 className="font-serif text-xl leading-tight" data-testid="eid-card-name">
              {student.firstName} {student.lastName}
            </h3>
            <p className="text-[10px] uppercase tracking-widest opacity-70 mt-1 font-mono" data-testid="eid-card-code">
              {student.studentCode}
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 relative">
          <CardField title="Curso" value={student.courseName} testId="eid-card-course" />
          <CardField title="Nivel" value={student.level || student.courseLevel} testId="eid-card-level" />
          <CardField title="Estado" value={label(student.status)} testId="eid-card-status" />
        </div>
        <div className="mt-5 flex items-end justify-between relative">
          <p className="text-[9px] uppercase tracking-widest opacity-55 max-w-[170px]">
            Escaneá el QR para verificar el carnet.
          </p>
          {publicUrl && (
            <div className="bg-[#F6EFDF] p-2 rounded-md" data-testid="eid-card-qr">
              <QRCodeSVG value={publicUrl} size={78} fgColor="#132C44" bgColor="#F6EFDF" level="M" />
            </div>
          )}
        </div>
      </div>
      {downloadable && (
        <button
          onClick={download}
          disabled={busy}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8AA986] text-[#132C44] px-4 py-2.5 text-[13px] font-semibold hover:bg-[#7a9a76] transition-colors disabled:opacity-60"
          data-testid="eid-card-download-btn"
        >
          <Download className="h-4 w-4" />
          {busy ? "Generando imagen…" : "Descargar carnet"}
        </button>
      )}
    </div>
  );
}
